import { pick } from "lodash";
import { UseFormReturn } from "react-hook-form";

import { UW_PERIL_NOTES } from "@covertree/consts";
import { UnderwritingQuestion } from "@covertree/new-business-graphql";
import {
  ConditionData,
  ConditionName,
  UnitLevelUnderwritingAnswers,
} from "@covertree/types";
import { sortBasedOnAnotherArr } from "@covertree/utils";

import { SupplementalHeatingForm } from "./modals/supplementalHeating";
import { BusinessOnPremisesForm } from "./modals/businessOnPremises";
import { TrampolineForm } from "./modals/trampoline";
import { SwimmingPoolForm } from "./modals/swimmingPool";
import { AnimalLiability } from "./modals/AnimalLiability";
import { InsuranceTroubleForm } from "./modals/insuranceTrouble";
import { BurglarAlarmForm } from "./modals/burglarAlarm";
import { StormMitigationFortifiedForm } from "./modals/StormMitigationFortified";
import { UnderwritingQuestionsForm } from "../../validatorsAndTypes/underwritingQuestions";
import {
  getUnderwritingQuestionModalContent,
  withPerilNote,
} from "../../utils/underwritingQuestions";

export type MappedUnderwritingQuestion = {
  key: keyof UnderwritingQuestionsForm;
  value: ConditionData;
};

export type QuestionModalProps = {
  questions?: Record<string, UnderwritingQuestion>;
  closeModal(): void;
};

const questionKeysSortOrder = [
  "unitIsTied",
  "thermoStaticControl",
  "utilityServices",
  "propertyWithFireProtection",
  "secureRails",
  "sourceOfHeat",
  "sourceOfHeatInstallation",
  "typeOfFuel",
  "pleaseDescribe",
  "unrepairedDamages",
  "businessOnPremises",
  "daycareOnPremises",
  "businessEmployeesOnPremises",
  "visitorsInAMonth",
  "businessDescription",
  "trampolineLiability",
  "trampolineLiabilityExtension",
  "trampolineSafetyNet",
  "swimmingPool",
  "fourFeetFence",
  "divingBoard",
  "divingBoardSlideLiabilityExtension",
  "animalBite",
  "conviction",
  "mortgage",
  "cancellationRenew",
  "reasonCancellationRenew",
  "wroughtIron",
  "burglarAlarm",
  "stormMitigationFortified",
  "stormMitigationImpactglass",
  "stormMitigationShutters",
  "automaticSprinkler",
];

const getUnderwritingQuestionsByState = (
  form: UseFormReturn<UnderwritingQuestionsForm>,
  questions: Record<string, UnderwritingQuestion>,
  state: string
): Partial<Record<UnitLevelUnderwritingAnswers, ConditionData>> => ({
  [UnitLevelUnderwritingAnswers.UnitIsTied]: {
    title: ConditionName.TiedDown,
    helpText: "Is the unit tied down?",
    tooltip:
      "Tie-downs refer to heavy-duty straps and anchors designed to stabilize homes during high winds.",
    image: "/images/conditions/look-right-for-home/tiedDown.webp",
    type: "option",
  },
  [UnitLevelUnderwritingAnswers.ThermoStaticControl]: {
    title: ConditionName.ThermostatControl,
    helpText: "Is the primary heat source thermostatically controlled?",
    tooltip:
      "A thermostat is a device is used to automatically heat the home within a constant temperature range.",
    image: "/images/conditions/look-right-for-home/thermostat.webp",
    type: "option",
  },
  [UnitLevelUnderwritingAnswers.UtilityServices]: {
    title: ConditionName.Utils,
    helpText: "Does the unit have active utility services?",
    tooltip:
      "Utility services refer to water, sewer, electricity, and gas (if needed for heat).",
    image: "/images/conditions/look-right-for-home/utilities.webp",
    type: "option",
  },
  [UnitLevelUnderwritingAnswers.PropertyWithFireProtection]: {
    title: ConditionName.NearPublicRoads,
    helpText:
      "Is the property protected by a fire protection service and accessible by the fire station via a road?",
    tooltip:
      "Fire Protection Service must have uninterrupted road access from the station to the home. If the home is on an island that requires boat/ferry access, it should be considered as being without fire protection service.",
    image: "/images/conditions/look-right-for-home/nearPublicRoads.webp",
    type: "option",
  },
  [UnitLevelUnderwritingAnswers.SecureRails]: {
    title: ConditionName.SecureRailings,
    helpText:
      "Do all steps, porches & decks that are 3' or more off the ground have secure rails?",
    tooltip:
      "Secure railings must be securely anchored, with no more than a 4 inch spacing between rails, when the drop is 3 feet or more off the ground.",
    image: "/images/conditions/look-right-for-home/secureRailings.webp",
    type: "option",
  },
  [UnitLevelUnderwritingAnswers.SourceOfHeat]: {
    title: ConditionName.SupplementalHeating,
    helpText: "Is a supplemental heating device used as a source of heat?",
    image: "/images/conditions/home/supplemental-heating.webp",
    tooltip:
      "Supplemental heating devices can include things like a wood stove intended to boost the heat of the home in addition to the primary heat source. Fireplaces are not considered supplemental heating devices.",
    type: "condition",
    questionKey: UnitLevelUnderwritingAnswers.SourceOfHeat,
    getContent: getUnderwritingQuestionModalContent(),
    getModalContent: (closeModal) => (
      <SupplementalHeatingForm
        questions={pick(questions, [
          UnitLevelUnderwritingAnswers.SourceOfHeatInstallation,
          UnitLevelUnderwritingAnswers.TypeOfFuel,
        ])}
        closeModal={closeModal}
      />
    ),
  },
  [UnitLevelUnderwritingAnswers.UnrepairedDamages]: {
    title: ConditionName.UnrepairedVisibleDamage,
    image: "/images/conditions/home/unrepaired-visible-damage.webp",
    helpText: "Does the unit have visible unrepaired damage?",
    tooltip:
      "Unrepaired damage includes any damage to the home that affects the functionality or structural integrity of the home. Common examples include missing shingles, water/rot damage, mold, broken windows (even if boarded up), and missing/damaged siding.",
    type: "condition",
  },
  [UnitLevelUnderwritingAnswers.BusinessOnPremises]: {
    title: ConditionName.BusinessOnPremises,
    helpText:
      "Is business conducted on the premises? This does not include “work from home”.",
    image: "/images/conditions/home/business-on-premises.webp",
    tooltip:
      "Business refers to the engagement of commercial or non-profit activities at the home. This can include things like day cares (whether run for a profit or not), retail stores, or use as a management office for a business on the premises. CoverTree does not provide commercial insurance.",
    type: "condition",
    questionKey: UnitLevelUnderwritingAnswers.BusinessOnPremises,
    getContent: getUnderwritingQuestionModalContent(),
    getModalContent: (closeModal) => (
      <BusinessOnPremisesForm
        questions={pick(questions, [
          UnitLevelUnderwritingAnswers.DaycareOnPremises,
          UnitLevelUnderwritingAnswers.BusinessEmployeesOnPremises,
          UnitLevelUnderwritingAnswers.VisitorsInAMonth,
        ])}
        closeModal={closeModal}
      />
    ),
  },
  [UnitLevelUnderwritingAnswers.TrampolineLiability]: {
    title: ConditionName.Trampoline,
    helpText:
      "Is there a trampoline on the premises? This does not include a community trampoline. ",
    image: "/images/conditions/home/trampoline.webp",
    tooltip:
      "The base CoverTree policy does NOT include coverage for Trampolines. However, it can be purchased for qualifying risks. Trampolines are a fun activity but also increase the risk for injury.",
    type: "condition",
    questionKey: UnitLevelUnderwritingAnswers.TrampolineLiability,
    getContent: withPerilNote(
      {
        text: UW_PERIL_NOTES.trampolineLiability,
        answers: [
          "trampolineLiability",
          "trampolineSafetyNet",
          "trampolineLiabilityExtension",
        ],
      },
      getUnderwritingQuestionModalContent()
    ),
    getModalContent: (closeModal) => (
      <TrampolineForm
        questions={pick(questions, [
          UnitLevelUnderwritingAnswers.TrampolineSafetyNet,
          UnitLevelUnderwritingAnswers.TrampolineLiabilityExtension,
        ])}
        closeModal={closeModal}
      />
    ),
  },
  [UnitLevelUnderwritingAnswers.SwimmingPool]: {
    title: ConditionName.SwimmingPool,
    helpText:
      "Is there a swimming pool on the premises? This does not include a community swimming pool.",
    image: "/images/conditions/home/swimming-pool.webp",
    tooltip:
      "The base CoverTree policy does include coverage for swimming pools. It however does NOT cover slides or diving boards. Slides and Diving Boards are a fun activity but also increase the risk for injury.",
    type: "condition",
    questionKey: UnitLevelUnderwritingAnswers.SwimmingPool,
    getContent: withPerilNote(
      {
        text: UW_PERIL_NOTES.divingBoardSlideLiability,
        answers: [
          "swimmingPool",
          "divingBoard",
          "divingBoardSlideLiabilityExtension",
        ],
      },
      getUnderwritingQuestionModalContent()
    ),
    getModalContent: (closeModal) => (
      <SwimmingPoolForm
        questions={pick(questions, [
          UnitLevelUnderwritingAnswers.FourFeetFence,
          UnitLevelUnderwritingAnswers.DivingBoard,
          UnitLevelUnderwritingAnswers.DivingBoardSlideLiabilityExtension,
        ])}
        closeModal={closeModal}
      />
    ),
  },
  [UnitLevelUnderwritingAnswers.AnimalBite]: {
    title: ConditionName.SpecialAnimals,
    modalTitle: UW_PERIL_NOTES.animalLiabilityExclusion,
    helpText: `Do you have any animals with a bite history/vicious propensities,${
      !["AZ", "GA", "IL"].includes(state)
        ? " or an Akita, Chow, Pit Bull, Doberman, Rottweiler,"
        : ""
    } or any non-domestic animal?`,
    image: "/images/conditions/special/specialDogs.webp",
    tooltip:
      "Vicious Propensities can include, but are not limited to, aggressive behavior towards other people or animals, training an animal to attack others (e.g. security), and attempted biting or other injury. An animal liability exclusion will be needed if any other this is true.",
    type: "condition",
    questionKey: UnitLevelUnderwritingAnswers.AnimalBite,
    getContent: withPerilNote({
      text: UW_PERIL_NOTES.animalLiabilityExclusion,
      answers: ["animalBite"],
    }),
    getModalContent: (closeModal) => (
      <AnimalLiability closeModal={closeModal} />
    ),
  },
  [UnitLevelUnderwritingAnswers.Conviction]: {
    title: ConditionName.LegalTrouble,
    image: "/images/conditions/special/legal.webp",
    helpText:
      "Have you been convicted of fraud, arson, or other insurance related offenses?",
    tooltip:
      "Legal trouble refers to when you have previously plead guilty or been convicted of fraud, arson, or any state or federal insurance related rules. It does not include other types of convictions.",
    type: "condition",
  },
  [UnitLevelUnderwritingAnswers.Mortgage]: {
    title: ConditionName.MortgageOrForeClosure,
    helpText:
      "Are you more than 60 days past due in mortgage payments or currently in foreclosure?",
    image: "/images/conditions/special/pastMortgagePayments.webp",
    tooltip:
      "This refers to consecutively missing loan payments for the home for more than 60 days or if the lender is in the process of taking ownership of the home.",
    type: "condition",
  },
  [UnitLevelUnderwritingAnswers.CancellationRenew]: {
    title: ConditionName.InsuranceTrouble,
    helpText:
      "Has your insurance been cancelled or non-renewed for underwriting reasons in the last 4 years?",
    image: "/images/conditions/special/insuranceTrouble.webp",
    tooltip:
      "Insurance Trouble refers to when another insurance company has chosen to cancel/non-renew your policy. It does not include when you cancel or non-renew, or if the cancellation is related to billing reasons.",
    type: "condition",
    questionKey: UnitLevelUnderwritingAnswers.CancellationRenew,
    getContent: getUnderwritingQuestionModalContent(),
    getModalContent: (closeModal) => (
      <InsuranceTroubleForm closeModal={closeModal} />
    ),
  },
  [UnitLevelUnderwritingAnswers.WroughtIron]: {
    title: ConditionName.IronBars,
    helpText:
      "Applies if every window and door is secured with wrought iron bars.",
    tooltip:
      "Bars may be made out of wrought iron, cast iron, steel, and high quality aluminum. It is highly recommended for the safety of occupants that Quick Release bars be utilized.",
    image: "/images/conditions/look-right-for-home/ironBars.webp",
    type: "condition",
  },
  [UnitLevelUnderwritingAnswers.BurglarAlarm]: {
    title: ConditionName.BurglarAlarm,
    modalTitle: "What kind of alarm do you have?",
    helpText: "Applies if you have a functioning burglar alarm for the home.",
    tooltip: `
      Local: Refers to an alarm system that produces a loud siren when
    triggered in an effort to notify neighbors and passerbys.
    Central/Monitored: Refers to an alarm system that notifies a
    professional monitoring center or the police directly.`,
    image: "/images/conditions/look-right-for-home/burglarAlarm.webp",
    type: "condition",
    questionKey: UnitLevelUnderwritingAnswers.BurglarAlarm,
    getContent: getUnderwritingQuestionModalContent(),
    getModalContent: (closeModal) => (
      <BurglarAlarmForm
        questions={pick(questions, [UnitLevelUnderwritingAnswers.BurglarAlarm])}
        closeModal={closeModal}
      />
    ),
  },
  [UnitLevelUnderwritingAnswers.StormMitigationFortified]: {
    title: ConditionName.IbhsFortified,
    helpText: "Does the home have an IBHS FORTIFIED Home certification?",
    image: "/images/conditions/home/storm-mitigation-fortified.webp",
    tooltip:
      "Homes with an IBHS FORTIFIED certificate may be eligible for a discount. Certificate must be provided.",
    type: "condition",
    questionKey: UnitLevelUnderwritingAnswers.StormMitigationFortified,
    getModalContent: (closeModal) => (
      <StormMitigationFortifiedForm
        questions={pick(questions, [
          UnitLevelUnderwritingAnswers.StormMitigationFortified,
        ])}
        closeModal={closeModal}
      />
    ),
    getContent: getUnderwritingQuestionModalContent(),
  },
  [UnitLevelUnderwritingAnswers.StormMitigationImpactglass]: {
    title: ConditionName.WindowsUtilizeGlass,
    helpText:
      "Do all the home’s windows utilize glass (or glazing) designed for hurricane force winds and accompanying debris and meet International Residence Code requirements?",
    image: "/images/conditions/home/storm-mitigation-impact-glass.webp",
    tooltip:
      "Impact Resistant Glass may qualify the home for a discount if ALL the home’s windows utilize glass (or glazing) designed for hurricane force winds and accompanying debris and meet International Residence Code requirements.",
    type: "condition",
  },
  [UnitLevelUnderwritingAnswers.StormMitigationShutters]: {
    title: ConditionName.WindowsUtilizeShutters,
    helpText:
      "Do all the home’s windows utilize shutters designed for hurricane force winds and accompanying debris and meet International Residence Code requirements?",
    image: "/images/conditions/home/storm-mitigation-storm-shutters.webp",
    tooltip:
      "Shutters may qualify the home for a discount if ALL the home’s windows utilize closable shutters designed for hurricane force winds and accompanying debris and meet International Residence Code requirements.",
    type: "condition",
  },
  [UnitLevelUnderwritingAnswers.AutomaticSprinkler]: {
    title: ConditionName.AutomaticSprinkler,
    helpText:
      "Do you have an automatic sprinkler system that meets NFPA B-D standards?",
    image: "/images/conditions/home/automatic-sprinkler-system.webp",
    tooltip:
      "The system will need to be installed according to the National Fire Protection Assocation standards.",
    type: "condition",
  },
});

export function getUnderwritingQuestions(
  form: UseFormReturn<UnderwritingQuestionsForm>,
  questions: Record<UnitLevelUnderwritingAnswers, UnderwritingQuestion>,
  type: "option" | "condition",
  state: string
): MappedUnderwritingQuestion[] {
  const underwritingQuestions = getUnderwritingQuestionsByState(
    form,
    questions,
    state
  );

  return Object.keys(questions)
    .map((x) => ({
      key: x as keyof UnderwritingQuestionsForm,
      value: underwritingQuestions[x as UnitLevelUnderwritingAnswers],
    }))
    .filter(({ value }) => value && value.type === type)
    .map(({ key, value }) => ({ key, value: value! }))
    .sort((item1, item2) =>
      sortBasedOnAnotherArr(item1.key, item2.key, questionKeysSortOrder)
    );
}
